import { createSelector } from "reselect";

import { BillingDocumentType, BillingRole } from "constants/billingDocuments";
import { BillingLedgerEntryStatus } from "constants/billingLedgerEntries";

import { selectBillingDocumentsAggridDataById } from "./billingDocuments";
import {
  BusinessTypes,
  selectBusinessesIdsInSaleByType,
} from "./businessesInSale";
import { selectIsLotLedgerEntryByLedgerEntryIdLookup } from "../ledgerEntries";
import { selectLedgerAccountByCodeLookup } from "../ledgerAccounts";
import { selectPaymentsByBillingDocumentIdLookup } from "../payments";
import { getLedgerEntries } from "../root";

const getCreditAndDebitAmount = (ledgerEntry, billingDocument = {}) => {
  // Categorise the ledger entry as a debit or credit (From the point of view of the LivestockAgent)
  // based on the billing document type and total amount.
  const { subtotal, taxAmount } = ledgerEntry;
  const total = parseFloat(subtotal) + parseFloat(taxAmount);
  let debitAmount = 0;
  let creditAmount = 0;

  if (billingDocument.type === BillingDocumentType.INVOICE) {
    // If it's an invoice (to a buyer) with a positive amount it's a credit.
    if (total > 0) {
      creditAmount = total;
    } else {
      debitAmount = Math.abs(total);
    }
  } else if (billingDocument.type === BillingDocumentType.RCTI) {
    // If it's an RCTI (from a vendor) with a negative amount it's a credit.
    if (total > 0) {
      debitAmount = total;
    } else {
      creditAmount = Math.abs(total);
    }
  }
  return { creditAmount, debitAmount };
};

/**
 * Resolves the effective billing role for each billing document.
 *
 * A document's role is determined by:
 *   1. The business's natural role in the sale (Buyer / Vendor) matched
 *      against the document type (Invoice / RCTI).
 *   2. If there's no natural-role match the document is classified as
 *      Other Debtor (Invoice) or Other Creditor (RCTI), optionally
 *      suffixed with a GL account name when all entries share one GL code.
 *
 * Keyed by billing document ID.
 */
export const selectBillingRoleByBillingDocumentIdLookup = createSelector(
  [
    getLedgerEntries,
    selectBillingDocumentsAggridDataById,
    selectBusinessesIdsInSaleByType,
    selectLedgerAccountByCodeLookup,
  ],
  (
    ledgerEntryByIdLookup,
    billingDocumentDataByIdLookup,
    businessesIdsByType,
    ledgerAccountByCode,
  ) => {
    // Pre-compute: if all entries for a document share one GL code, record its
    // name for use in "Other Debtor/Creditor" labels.
    const singleGlNameByDocumentId = Object.values(
      ledgerEntryByIdLookup,
    ).reduce((acc, entry) => {
      const glCode = entry.glCode ?? null;
      if (!(entry.documentId in acc)) {
        acc[entry.documentId] = glCode;
      } else if (acc[entry.documentId] !== glCode) {
        acc[entry.documentId] = null; // mixed GL codes
      }
      return acc;
    }, {});
    Object.keys(singleGlNameByDocumentId).forEach(docId => {
      const glCode = singleGlNameByDocumentId[docId];
      singleGlNameByDocumentId[docId] = glCode
        ? ledgerAccountByCode[glCode]?.name ?? null
        : null;
    });

    const buyerBusinessIds = new Set([
      ...(businessesIdsByType[BusinessTypes.BUYER] || []),
      ...(businessesIdsByType[BusinessTypes.BUYER_SPLIT] || []),
      ...(businessesIdsByType[BusinessTypes.INVOICEE] || []),
      ...(businessesIdsByType[BusinessTypes.THIRD_PARTY] || []),
    ]);
    const vendorBusinessIds = new Set([
      ...(businessesIdsByType[BusinessTypes.VENDOR] || []),
      ...(businessesIdsByType[BusinessTypes.VENDOR_SPLIT] || []),
    ]);

    return Object.values(billingDocumentDataByIdLookup).reduce(
      (acc, { billingDocument, business }) => {
        const isInvoice = billingDocument.type === BillingDocumentType.INVOICE;
        const isRcti = billingDocument.type === BillingDocumentType.RCTI;
        const isNaturalBuyer = buyerBusinessIds.has(business?.id);
        const isNaturalVendor = vendorBusinessIds.has(business?.id);

        let billingRole;
        if (isNaturalBuyer && isInvoice) {
          billingRole = BillingRole.BUYER;
        } else if (isNaturalVendor && isRcti) {
          billingRole = BillingRole.VENDOR;
        } else if (isInvoice) {
          const glName = singleGlNameByDocumentId[billingDocument.id];
          billingRole = glName
            ? `${BillingRole.OTHER_DEBTOR} - ${glName}`
            : BillingRole.OTHER_DEBTOR;
        } else if (isRcti) {
          const glName = singleGlNameByDocumentId[billingDocument.id];
          billingRole = glName
            ? `${BillingRole.OTHER_CREDITOR} - ${glName}`
            : BillingRole.OTHER_CREDITOR;
        } else {
          billingRole = "Other";
        }

        acc[billingDocument.id] = billingRole;
        return acc;
      },
      {},
    );
  },
);

export const selectLedgerEntryData = createSelector(
  [
    getLedgerEntries,
    selectBillingDocumentsAggridDataById,
    selectPaymentsByBillingDocumentIdLookup,
    selectIsLotLedgerEntryByLedgerEntryIdLookup,
    selectBillingRoleByBillingDocumentIdLookup,
  ],
  (
    ledgerEntryByIdLookup,
    billingDocumentDataByIdLookup,
    paymentsByBillingDocumentIdLookup,
    isLotLedgerEntryByIdLookup,
    billingRoleByDocumentId,
  ) => {
    return Object.values(ledgerEntryByIdLookup)
      .filter(ledgerEntry => {
        // filter out ledger entries that aren't relevant to the visible documents
        // i.e. we don't have a corresponding document in state.
        // ref BillingDocumentQuerySet.chargeable() filter used in BillingDocumentViewSet.list
        const { billingDocument } =
          billingDocumentDataByIdLookup[ledgerEntry.documentId] || {};

        return Boolean(billingDocument);
      })
      .map(ledgerEntry => {
        const {
          billingDocument,
          business,
          effectiveVendorCommissions,
          vendorCommissionBands,
        } = billingDocumentDataByIdLookup[ledgerEntry.documentId] || {};

        const payments =
          paymentsByBillingDocumentIdLookup[billingDocument?.id] || [];

        const creditAndDebitAmount = getCreditAndDebitAmount(
          ledgerEntry,
          billingDocument,
        );

        const qty = parseFloat(ledgerEntry.quantity);
        const head = isLotLedgerEntryByIdLookup[ledgerEntry.id]
          ? Number.isFinite(qty)
            ? qty
            : null
          : null;

        const { creditAmount, debitAmount } = creditAndDebitAmount;
        const netAmount = creditAmount - debitAmount;

        const billingRole =
          billingRoleByDocumentId[billingDocument?.id] ?? null;

        const businessAndDocument =
          business?.name && billingDocument?.invoiceNumber
            ? `${business.name} / ${billingDocument.invoiceNumber}`
            : business?.name ?? billingDocument?.invoiceNumber ?? null;

        return {
          billingDocument: {
            ...billingDocument,
            billingRole,
            businessAndDocument,
          },
          business,
          ledgerEntry: Object.assign({}, ledgerEntry, creditAndDebitAmount, {
            head,
            netAmount,
          }),
          payments,
          effectiveVendorCommissions,
          vendorCommissionBands,
        };
      });
  },
);

export const selectCommittedLedgerEntryData = createSelector(
  [selectLedgerEntryData],
  ledgerEntryData =>
    ledgerEntryData.filter(
      ({ ledgerEntry }) =>
        ledgerEntry.status === BillingLedgerEntryStatus.COMMITTED,
    ),
);
